import React from "react";
import { Animated, Platform, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import colors from "../../constants/colors";

/** Matches stack default HeaderTitle sizing so typography stays consistent. */
const FONT_STYLES = Platform.select({
  ios: {
    fontSize: 17,
    fontWeight: "600",
  },
  android: {
    fontSize: 20,
    fontFamily: "sans-serif-medium",
    fontWeight: "normal",
  },
  default: {
    fontSize: 18,
    fontWeight: "500",
  },
});

/**
 * Stack navigator default HeaderTitle sets numberOfLines: 1, which truncates long form names.
 * This title wraps to multiple lines and expands the navigation header height to fit measured text.
 */
export default function FormHeaderTitle({
  children,
  onLayout,
  allowFontScaling,
  tintColor,
  style,
}) {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const lastTotalRef = React.useRef(0);

  const handleLayout = React.useCallback(
    (e) => {
      if (typeof onLayout === "function") onLayout(e);

      const textHeight = e.nativeEvent.layout.height;
      const rowMin = Platform.select({ ios: 44, android: 56, default: 56 });
      const rowPad = Platform.OS === "web" ? 16 : 10;
      const titleRowHeight = Math.max(rowMin, textHeight + rowPad);
      const total = insets.top + titleRowHeight;

      if (Math.abs(total - lastTotalRef.current) < 4) return;
      lastTotalRef.current = total;

      navigation.setOptions({
        headerStyle: {
          backgroundColor: colors.primary,
          height: total,
        },
      });
    },
    [navigation, onLayout, insets.top],
  );

  const extraProps =
    Platform.OS === "android" ? {} : { "aria-level": 1 };

  return (
    <Animated.Text
      accessibilityRole="header"
      {...extraProps}
      allowFontScaling={allowFontScaling}
      onLayout={handleLayout}
      style={[
        FONT_STYLES,
        styles.wrap,
        tintColor !== undefined ? { color: tintColor } : null,
        style,
      ]}
    >
      {children}
    </Animated.Text>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flexShrink: 1,
    textAlign: "center",
    width: "100%",
    ...(Platform.OS === "web"
      ? { whiteSpace: "normal", wordBreak: "break-word" }
      : {}),
  },
});
